import { Spinner } from "@/components/ui/spinner";
import { useDiffWorker } from "@/hooks/useDiffWorker";

interface DiffViewProps {
  oldContent: string;
  newContent: string;
}

export function DiffView({ oldContent, newContent }: DiffViewProps) {
  const { status, chunks, hasAnyChange } = useDiffWorker(
    oldContent,
    newContent,
  );

  return (
    <div className="grid grid-cols-2 gap-0 border rounded-lg overflow-hidden">
      <div className="border-b bg-muted/50 px-4 py-2 text-sm font-medium">
        Previous Version
      </div>
      <div className="border-b bg-muted/50 px-4 py-2 text-sm font-medium">
        Current Version
      </div>

      {status === "pending" ? (
        <>
          <div className="overflow-auto max-h-[60dvh] flex items-center justify-center p-8">
            <Spinner
              variant="circle"
              className="text-muted-foreground"
              size={24}
            />
          </div>
          <div className="border-l overflow-auto max-h-[60dvh] flex items-center justify-center p-8">
            <Spinner
              variant="circle"
              className="text-muted-foreground"
              size={24}
            />
          </div>
        </>
      ) : status === "error" ? (
        <>
          <div className="p-4 text-sm text-muted-foreground text-center border-b">
            Failed to compute diff.
          </div>
          <div className="border-l p-4 text-sm text-muted-foreground text-center">
            Failed to compute diff.
          </div>
        </>
      ) : !hasAnyChange ? (
        <>
          <div className="p-4 text-sm text-muted-foreground text-center border-b">
            No differences found between these versions.
          </div>
          <div className="border-l p-4 text-sm text-muted-foreground text-center">
            Content is identical.
          </div>
        </>
      ) : (
        <>
          <div className="overflow-auto max-h-[60dvh]">
            <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words">
              {chunks.map((chunk, i) =>
                chunk.changeType === "removed" ? (
                  <span
                    key={`o-${i}`}
                    className="bg-red-100/60 dark:bg-red-900/30 text-red-700 dark:text-red-300 line-through"
                  >
                    {chunk.value}
                  </span>
                ) : (
                  <span key={`u-${i}`} className="text-muted-foreground">
                    {chunk.value}
                  </span>
                ),
              )}
            </pre>
          </div>
          <div className="border-l overflow-auto max-h-[60dvh]">
            <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words">
              {chunks.map((chunk, i) =>
                chunk.changeType === "added" ? (
                  <span
                    key={`a-${i}`}
                    className="bg-green-100/60 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                  >
                    {chunk.value}
                  </span>
                ) : (
                  <span key={`u2-${i}`} className="text-muted-foreground">
                    {chunk.value}
                  </span>
                ),
              )}
            </pre>
          </div>
        </>
      )}
    </div>
  );
}
